import { AbstractControl } from '@angular/forms';
import { DropdownItem } from '@shared/components/cinico-dropdown/dropdown.component';
import { ButtonValue } from '@shared/components/toggle-buttons/toggle-buttons.component';
import { API_URLS } from 'app/modules/core/constants/core.constants';
import { environment } from 'environments/environment';

export const flattenObject = (obj: any) => {
  const result: any = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        const temp: any = flattenObject(obj[key]);

        for (const nestedKey in temp) {
          if (Object.prototype.hasOwnProperty.call(temp, nestedKey)) {
            result[nestedKey] = temp[nestedKey];
          }
        }
      } else {
        if (obj[key] !== undefined) {
          result[key] = obj[key];
        }
      }
    }
  }
  return result;
};

export const isEmpty = (obj: Record<string, any>) => {
  const b = Object.keys(obj).length === 0;
  return b;
};

export const trimWhiteSpace = (value: any) => {
  let trimedObj = value;
  if (typeof trimedObj === 'string') trimedObj = trimedObj.trim();
  else {
    if (typeof trimedObj === 'object')
      for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          const element = value[key];
          trimedObj[key] = typeof element === 'string' ? element.trim() : element;
        }
      }
  }

  return trimedObj;
};

// deep copy of flat arrays
export function copyArray<T>(value: T[]) {
  return value.map<T>(item => {
    return { ...item };
  });
}

export function setBtnsValue(value: string, list: ButtonValue[]) {
  if (value && list?.length > 0) {
    return list.map(btn => {
      btn.isActive = btn.value === value;
      return btn;
    });
  }

  return list;
}

export function setDropdownItems<T>(items: T, prop?: string, id?: string) {
  if (!items) return [];
  return (items as [])?.map((item, index) => {
    return { key: id ? item[id] : index, value: prop ? item[prop] : item } as DropdownItem;
  });
}

export function setDropdownItemsFromNumericEnum(enumObject: any): DropdownItem[] {
  return Object.keys(enumObject)
    .filter(key => isNaN(Number(key)))
    .map(key => {
      return {
        key: enumObject[key],
        value: key
      } as DropdownItem;
    });
}

// created because yes/no question
export const setInvalidClasses = (control: AbstractControl) => {
  const status = control.touched && control.invalid;
  return {
    'ng-invalid': status,
    'ng-touched': status
  };
};

export function convertBase64ToBlob(base64: string): Blob {
  const parts = base64.split(';base64,');
  const mimeType = parts[0].split(':')[1];
  const base64Data = parts[1];

  const binaryData = atob(base64Data);

  const arrayBuffer = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    arrayBuffer[i] = binaryData.charCodeAt(i);
  }

  return new Blob([arrayBuffer], { type: mimeType });
}

export function convertBase64ToFile(dataurl: string, filename: string): File {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

export function convertBlobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject('Error reading blob');
    };

    reader.readAsDataURL(blob);
  });
}

export function getThumb(width: number, height: number, imageName: string): string {
  return `${environment.baseApiUrl}/${API_URLS.images}/thumbs/${width}/${height}/${imageName}`;
}

export const dataURLtoFile = (dataurl: string, filename: string) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);

  let n = bstr.length;
  let u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

export const getImage = (dataUrl: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = dataUrl;
    image.onload = () => {
      resolve(image);
    };
    image.onerror = (el: any, err: any) => {
      reject(err.error);
    };
  });
};

export const compressImage = async (
  dataUrl: string,
  imageType: string,
  resolution: number,
  quality: number
): Promise<string> => {
  // Create a temporary image so that we can compute the height of the image.
  const image = await getImage(dataUrl);
  const oldWidth = image.naturalWidth;
  const oldHeight = image.naturalHeight;

  const longestDimension = oldWidth > oldHeight ? 'width' : 'height';
  const currentRes = longestDimension == 'width' ? oldWidth : oldHeight;

  if (currentRes < resolution) {
    resolution = currentRes;
  }

  // Calculate new dimensions
  const newSize =
    longestDimension == 'width'
      ? Math.floor((oldHeight / oldWidth) * resolution)
      : Math.floor((oldWidth / oldHeight) * resolution);
  const newWidth = longestDimension == 'width' ? resolution : newSize;
  const newHeight = longestDimension == 'height' ? resolution : newSize;

  // Create a temporary canvas to draw the downscaled image on.
  const canvas = document.createElement('canvas');
  canvas.width = newWidth;
  canvas.height = newHeight;

  // Draw the downscaled image on the canvas and return the new data URL.
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(image, 0, 0, newWidth, newHeight);
  const newDataUrl = canvas.toDataURL(imageType, quality);
  return newDataUrl;
};

export const compressImageFile = (file: File): Promise<File> => {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64ToCompress = reader.result as string;
      const base64 = await compressImage(base64ToCompress, 'image/jpeg', 1000, 0.6);
      resolve(dataURLtoFile(base64, file.name));
    };
  });
};

export const checkIfFileIsImageByFileType = (fileType: string): boolean => {
  return fileType.toLowerCase().startsWith('image/');
};

export const checkIfFileIsImageByFileName = (fileName: string): boolean => {
  const fName = fileName.toLowerCase();
  return (
    fName.endsWith('.bmp') ||
    fName.endsWith('.emf') ||
    fName.endsWith('.exif') ||
    fName.endsWith('.dib') ||
    fName.endsWith('.gif') ||
    fName.endsWith('.icon') ||
    fName.endsWith('.jpg') ||
    fName.endsWith('.jpeg') ||
    fName.endsWith('.jpe') ||
    fName.endsWith('.jfif') ||
    fName.endsWith('.png') ||
    fName.endsWith('.pnz') ||
    fName.endsWith('.tiff') ||
    fName.endsWith('.svg') ||
    fName.endsWith('.webp') ||
    fName.endsWith('.wmf')
  );
};

export const toNameFormat = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const shortenString = (fullID: string, length: number): string => {
  if (fullID.length > length) fullID = fullID.slice(0, length) + '...';
  return fullID;
};

export function convertDateTimeFormat(datetimeString: string): string {
  const regex = /(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2}):(\d{2})/;
  const match = datetimeString.match(regex);

  if (!match) {
    console.error(`Input string does not match the required format 'MM/dd/yyyy HH:mm:ss': ${datetimeString}`);
    return 'Invalid date format';
  }

  const [, month, day, year, hour24Str, minute] = match;
  const hour24 = parseInt(hour24Str, 10);
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  const hour12Str = String(hour12).padStart(2, '0');

  return `${month}/${day}/${year} ${hour12Str}:${minute} ${period}`;
}

export const formatCurrency = (currencyValue: string | number): string => {
  const num = typeof currencyValue === 'string' ? parseFloat(currencyValue) : currencyValue;

  if (isNaN(num) || !isFinite(num)) {
    console.error(`Attempted to format an invalid number: ${currencyValue}`);
    return 'Invalid Number';
  }

  const formatter = new Intl.NumberFormat('en-US', { style: 'decimal' });
  return formatter.format(num);
};

export const parseCurrency = (currencyString: string): number => {
  var num: number = 0;

  if (typeof currencyString === 'string' && currencyString.startsWith('$')) {
    const cleaned = currencyString.replace(/[^0-9.]/g, '');
    num = parseFloat(cleaned);
  }

  return isNaN(num) ? 0 : num;
};

export const extractDefinedValues = (input: string, definedSet: string[]): string[] => {
  const result: string[] = [];
  const sortedSet = [...definedSet].sort((a, b) => b.length - a.length);

  let i = 0;
  while (i < input.length) {
    let matched = false;
    for (const value of sortedSet) {
      if (input.startsWith(value, i)) {
        result.push(value);
        i += value.length;
        matched = true;
        break;
      }
    }
    if (!matched) i++;
  }

  return result;
};
